import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useRowSelectionCount } from "@/components/data-table/hooks/useRowSelectionCount";
import { QueryTableMenubar } from "@/components/query-table/components/QueryTableMenubar";
import { useAutoSyncedRowSelection } from "@/components/query-table/hooks/useAutoSyncedRowSelection";
import { useSelectedRowIds } from "@/components/query-table/hooks/useSelectedRowIds";
import { StandardInMemoryTable } from "@/components/in-memory-table/StandardInMemoryTable";
import { Module } from "@/constants";
import { EmailTypeAllowedRolesTableActions } from "@/modules/email-types/components/EmailTypeAllowedRolesTableActions";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { displayEmailTypeRole } from "@/schemas/emailType";
import { EmailTypeRole } from "@/types/emailType";
import { useCallback, useEffect } from "react";

const TABLE_NAME = "emailTypeAllowedRoles";

interface EmailTypeAllowedRolesTableProps {
  roles: EmailTypeRole[];
  isSubmitting: boolean;
  onOpenAddRoles: () => void;
  onRemoveRoles: (roleIds: string[]) => void;
}

export const EmailTypeAllowedRolesTable = ({
  roles,
  isSubmitting,
  onOpenAddRoles,
  onRemoveRoles,
}: EmailTypeAllowedRolesTableProps) => {
  const { can_update } = useModulePermissions(Module.EmailTypes);
  const options = useCreateDataTableOptions<EmailTypeRole>(TABLE_NAME);
  const { store } = useRowSelectionContext();
  const { rowSelection, setRowSelection } = useAutoSyncedRowSelection({
    enabled: can_update,
  });
  const selectedCount = useRowSelectionCount();

  // Clear selection when role rows change.
  useEffect(() => {
    store.clear();
  }, [roles, store]);

  const selectedRowIds = useSelectedRowIds({ rowSelection });

  const handleRemoveSelected = useCallback(() => {
    if (selectedRowIds.size === 0) {
      return;
    }
    onRemoveRoles(Array.from(selectedRowIds));
  }, [onRemoveRoles, selectedRowIds]);

  const actionsProps = {
    selectedCount,
    isSubmitting,
    onRemoveSelected: handleRemoveSelected,
    onOpenAddRoles,
  };

  return (
    <StandardInMemoryTable<EmailTypeRole>
      schema={displayEmailTypeRole}
      name={TABLE_NAME}
      translationKey="emailTypes"
      items={roles}
      options={options}
      rowSelection={can_update ? rowSelection : undefined}
      setRowSelection={can_update ? setRowSelection : undefined}
      tableFeatures={{
        enableRowSelection: can_update,
        enableSelectAll: can_update,
      }}
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-row items-center justify-between gap-2 py-2">
          <QueryTableMenubar className="mb-0 min-w-0 grow">
            <QueryTableMenubar.Columns />
            <QueryTableMenubar.Settings />
          </QueryTableMenubar>
          {can_update ? (
            <div className="flex shrink-0 flex-wrap gap-2">
              <EmailTypeAllowedRolesTableActions
                variant="outline"
                testIds={{
                  removeSelected: "email-types-remove-selected-roles-button",
                  addRoles: "email-types-open-add-roles-dialog-button",
                }}
                {...actionsProps}
              />
            </div>
          ) : null}
        </div>

        <StandardInMemoryTable.Content>
          <StandardInMemoryTable.Status className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {can_update ? (
              <EmailTypeAllowedRolesTableActions
                variant="secondary"
                {...actionsProps}
              />
            ) : null}
          </StandardInMemoryTable.Status>
        </StandardInMemoryTable.Content>
      </div>
    </StandardInMemoryTable>
  );
};
